import {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import FormInput from "../../../../inputs/FormInput.tsx";
import {InputType} from "../../../../../types/input.ts";
import Button from "../../../../buttons/Button.tsx";
import {ButtonSize, ButtonType, ButtonVariant} from "../../../../../types/button.ts";
import {IAddPromotionBannerRequest} from "../../../../../request/marketing/addPromotionBannerRequest.ts";
import apiNet from "../../../../../newApi/apiNet.ts";
import {useChannelsService} from "../../../../../hooks/useChannelsService.tsx";
import DateHelper from "../../../../../helpers/dateHelper.ts";
import {IPromotionBanner} from "../../../../../interfaces/templates/bannerHome/IPromotionBanner.ts";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../../../../providers/NotificationProvider.tsx";
import {NotificationStatus} from "../../../../notification/Notification.tsx";
import imageCompression from 'browser-image-compression';
import CircleLoader from '../../../../loaders/CircleLoader.tsx';

interface IProps {
    mode: "edit" | "create";
    bannerData?: IPromotionBanner;
}

const BannersTemplate1Form = ({mode, bannerData}: IProps) => {
    const {banner_id} = useParams();
    const _navigate = useNavigate();
    const {showNotification} = useNotification();
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState(""); // this is used in edit mode
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [minDateTime, setMinDateTime] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [priority, setPriority] = useState(0);
    const [selectedChannelId, setSelectedChannelId] = useState(0);
    const [showErrors, setShowErrors] = useState(false);
    const [fileError, setFileError] = useState(false);
    const [startDateError, setStartDateError] = useState(true);
    const [endDateError, setEndDateError] = useState(true);
    const [titleError, setTitleError] = useState(true);


    const channelsService = useChannelsService();

    const handleTitleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTitle(e.currentTarget.value);
    }

    const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
        setDescription(e.currentTarget.value);
    }

    const handlePriorityChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPriority(Number(e.currentTarget.value));
    }

    const handleFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsProcessingFiles(true);
            const fileType = file.type;
            const fileSize = file.size;

            const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
            const maxSize = 5 * 1024 * 1024; // 5MB

            if (!validTypes.includes(fileType)) {
                // setError('Invalid file type. Only JPEG and PNG are allowed.');
                setFile(null);
                return;
            }

            if (fileSize > maxSize) {
                // setError('File size exceeds 5MB.');
                setFile(null);
                return;
            }
            const compresedFile = await compressImage(file);
            // setError(null);
            setFile(compresedFile);
            setIsProcessingFiles(false);
            // setImageFileName(file.name);
        }
    }

    const compressImage = async (file: File): Promise<File | null> => {
        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            initialQuality: 0.8,
            fileType: 'image/webp',
        }
        try {
            const compressedBlob = await imageCompression(file, options);
            const compressedFile = new File(
                [compressedBlob],
                file.name.replace(/\.\w+$/, '.webp'),
                {type: 'image/webp'}
            );
            return compressedFile;
        } catch (error) {
            return null;
        }
    }


    const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedCategory(event.target.value);
    };

    const handleChannelIdChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedChannelId(Number(event.target.value));
    };

    const handleStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEndDate(event.target.value);
    };


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        validateForm();
        if (!isFormValid()) {
            setShowErrors(true);
            return;
        }
        if (mode === "create") {
            await createBanner();
        }
        if (mode === "edit") {
            await updateBanner();
        }
    }

    const updateBanner = async () => {
        try {
            const formData = new FormData();
            const addNewBannerRequest: IAddPromotionBannerRequest = {
                title,
                priority,
                startDate,
                endDate,
                templateId: 1,
                templateContentDetails: {
                    description: description,
                    category: selectedCategory,
                    channelId: selectedChannelId,
                }
            };
            if (file === null && imageUrl !== null)
                addNewBannerRequest.templateContentDetails.image = imageUrl;
            formData.append("content", JSON.stringify(addNewBannerRequest));
            if (file !== null)
                formData.append("image", file);
            if (banner_id)
                await apiNet.updatePromotionBanner(formData, banner_id);
            showNotification("Sukces", "Baner został zaktualizowany pomyślnie.", NotificationStatus.success);
            _navigate("/konto/banery?view=ALL_BANNERS");
        } catch (e) {
            showNotification("Niepowodzenie", "Wystąpił bład po stronie serwera. Proszę spróbować ponownie za kilka minut.", NotificationStatus.failure);
        }
    }

    const createBanner = async () => {
        const formData = new FormData();
        try {
            const addNewBannerRequest: IAddPromotionBannerRequest = {
                title,
                priority,
                startDate,
                endDate,
                templateId: 1,
                templateContentDetails: {
                    description: description,
                    category: selectedCategory,
                    channelId: selectedChannelId
                }
            };
            formData.append("content", JSON.stringify(addNewBannerRequest));
            if (file !== null) {
                formData.append("image", file);
            }
            await apiNet.addPromotionBanner(formData);
            showNotification("Sukces", "Baner został dodany pomyślnie.", NotificationStatus.success);
            _navigate("/konto/banery?view=ALL_BANNERS");
        } catch (e) {
            showNotification("Niepowodzenie", "Wystąpił bład po stronie serwera. Proszę spróbować ponownie za kilka minut.", NotificationStatus.failure);
        }
    }

    const isFormValid = (): boolean => {
        if (titleError || startDateError || endDateError) {
            return false;
        }
        return true;
    }
    const validateForm = (): void => {
        setTitleError(title.length < 3);
        setStartDateError(startDate == null);
        setEndDateError(endDate == null);
        setFileError(file == null);
    }

    useEffect(() => {
        setMinDateTime(DateHelper.GetActualTimeForInputDate());
    }, []);

    useEffect(() => {
        if (channelsService.channels.length == 0)
            channelsService.getChannels()
    }, [channelsService.channels]);

    useEffect(() => {
        if (bannerData && bannerData.templateContentDetails && bannerData.templateContentDetails.description) {
            setTitle(bannerData?.title);
            if (bannerData.templateContentDetails.description)
                setDescription(bannerData?.templateContentDetails.description.toString());
            setPriority(bannerData?.priority);
            setStartDate(bannerData?.startDate);
            setEndDate(bannerData?.endDate);
            setSelectedCategory(bannerData?.templateContentDetails.category.toString());
            if (bannerData.templateContentDetails.channelId)
                setSelectedChannelId(Number(bannerData.templateContentDetails.channelId));
            setImageUrl(bannerData.templateContentDetails.image.toString());
            if (bannerData.templateContentDetails.image)
                setFileName(bannerData.templateContentDetails.image.toString().split('/').pop() || "");
            validateForm();
        }
    }, [bannerData]);

    return (
        <form action="" onSubmit={handleSubmit} className={"space-y-3"}>
            <label className={"flex flex-col"} htmlFor="options">Wybierz kategorie:
                <select
                    className={`appearance-none bg-neutral-600 rounded-full text-white border border-neutral-700 px-3 py-2 mt-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] ring-offset-neutral-800 outline-none transition-all`}
                    id="options" value={selectedCategory} onChange={handleSelectChange}>
                    <option value="">-- Wybierz kategorie --</option>
                    <option value="Dla dzieci">Dla dzieci</option>
                    <option value="Dokument">Dokument</option>
                    <option value="Kuchnia">Kuchnia</option>
                    <option value="Mega hit">Mega hit</option>
                    <option value="Na żywo">Na żywo</option>
                    <option value="Nie przegap">Nie przegap</option>
                    <option value="Podróże">Podróże</option>
                    <option value="Premiera">Premiera</option>
                    <option value="Przyroda">Przyroda</option>
                    <option value="Rozrywka">Rozrywka</option>
                    <option value="Serial">Serial</option>
                    <option value="Sport">Sport</option>
                </select>
            </label>
            <FormInput className="ring-offset-neutral-800 py-2 bg-neutral-600" label="Tytuł" name="title"
                       onInputChange={handleTitleInputChange} type={InputType.TEXT} value={title}/>
            {showErrors && titleError &&
                <p className={"text-red-500"}>Tytuł jest wymagany</p>
            }
            <FormInput className="ring-offset-neutral-800 py-2 bg-neutral-600" label="Opis" name="description"
                       onInputChange={handleDescriptionChange} type={InputType.TEXT} value={description}/>
            <label htmlFor="priority" className={"block"}>
                <p className={"mb-2"}>Priorytet</p>
                <input id={"priority"} type="number" min={0} max={999} value={priority} onChange={handlePriorityChange}
                       className={"appearance-none text-white border border-neutral-700 px-3 mt-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] outline-none transition-all rounded-full ring-offset-neutral-800 py-2 bg-neutral-600 w-full"}/>
            </label>
            <label htmlFor="startDate" className={"block"}>
                <p className="mb-2">Widoczny od</p>
                <input type="datetime-local" name="endDate" id="startDate" value={startDate}
                       onChange={handleStartDateChange}
                       className={`appearance-none w-full bg-neutral-600 rounded-full text-white border border-transparent px-3 py-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] ring-offset-neutral-800 outline-none transition-all`}/>
            </label>
            {showErrors && startDateError &&
                <p className={"text-red-500"}>Data rozpoczęcia jest wymagana.</p>
            }
            <label htmlFor="endDate" className={"block"}>
                <p className="mb-2">Widoczny do</p>
                <input type="datetime-local" name="endDate" min={minDateTime} id="endDate" value={endDate}
                       onChange={handleEndDateChange}
                       className={`appearance-none w-full bg-neutral-600 rounded-full text-white border border-transparent px-3 py-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] ring-offset-neutral-800 outline-none transition-all`}/>
            </label>
            {showErrors && endDateError &&
                <p className={"text-red-500"}>Data zakończenia jest wymagana</p>
            }
            <label className={"flex flex-col"} htmlFor="channel">Wybierz kanał:
                <select
                    className={`appearance-none bg-neutral-600 rounded-full text-white border border-neutral-700 px-3 py-2 mt-2 ring-2 ring-offset-2 ring-transparent focus:ring-[#008DD2] ring-offset-neutral-800 outline-none transition-all`}
                    id="channel" value={selectedChannelId} onChange={handleChannelIdChange}>
                    <option value="">-- Wybierz kanał --</option>
                    {channelsService.channels.map((channel) =>
                        <option key={channel.ch_id} value={channel.ch_id}>{channel.name}</option>
                    )}
                </select>
            </label>
            <div className="flex items-center w-full">
                <label htmlFor={"image"}
                       className="flex flex-col items-center my-2 px-4 py-6 bg-neutral-900 text-[#008DD2] rounded-lg w-full shadow-lg tracking-wide uppercase border border-[#008DD2] cursor-pointer hover:bg-[#008DD2] hover:text-white transition-colors">
                    {isProcessingFiles ?
                        <>
                            <CircleLoader/>
                            <span className="mt-2 text-base leading-normal">Przetwarzanie pliku...</span>
                        </>
                        :
                        <>
                            <svg className="w-8 h-8" fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                                 viewBox="0 0 20 20">
                                <path
                                    d="M16.5 8.5c0 .3-.1.5-.3.7l-8 8c-.4.4-1 .4-1.4 0l-4-4c-.4-.4-.4-1 0-1.4s1-.4 1.4 0L8 14.6l7.3-7.3c.4-.4 1-.4 1.4 0 .2.2.3.5.3.7zM10 3H8v5H3v2h5v5h2v-5h5V8h-5V3z"/>
                            </svg>
                            <span
                                className="mt-2 text-base leading-normal">{file?.name && file.name}{!file && fileName && fileName}{!file && !fileName && "WYBIERZ PLIK"}</span>
                            <span
                                className="mt-2 text-base tracking-normal lowercase">{file?.name || fileName ? "" : "Zalecane zdjęcie w rozdzielczości 1920x1080"}</span>
                        </>
                    }
                    <input type='file' name={"image"} id={"image"} className="hidden" accept=".jpg,.png,.webp"
                           onChange={handleFileInputChange}/>
                </label>
            </div>
            {showErrors && fileError &&
                <p className={"text-red-500"}>Banner jest wymagany</p>
            }
            <Button className="mt-4 ring-offset-neutral-800 py-1.5 pl-10 pr-10 w-full" size={ButtonSize.LARGE}
                    variant={ButtonVariant.PRIMARY}
                    type={ButtonType.SUBMIT}>{mode === "create" && "Dodaj"} {mode === "edit" && "Zapisz"}</Button>
        </form>
    );
};

export default BannersTemplate1Form;